import React, {useState} from "react";
import {
    CircularProgress,
    Grid,
    Typography,
} from "@mui/material";
import {Locale} from "../../../locales/Locale";
import {IdlocaGeneric} from "../../../locales/IdlocaGeneric";
import {IMotorcycle} from "../../../interfaces/Interfaces";
import {useNavigate} from "react-router-dom";
import {useCompare} from "../compare/CompareContext";
import {CatalogueTableItem} from "./CatalogueTableItem";
import {InfoMessage} from "../../table/InfoMessage";
import {CustomSnackbar} from "../../table/CustomSnackbar";
import {ViewTableStyle} from "../../../enums/ViewTableStyle";

interface ICatalogueContent {
    motorcycles: IMotorcycle[] | null;
    dataLoading: boolean;
    viewStyle: ViewTableStyle;
    favourites: number[];
    toggleFavourites: (id: number) => void;
}

export const CatalogueContent: React.FC<ICatalogueContent> = ({
    motorcycles,
    dataLoading,
    viewStyle,
    favourites,
    toggleFavourites,
}) => {
    const {compareArray, toggleCompareArray} = useCompare();

    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<React.ReactNode>("");
    const navigate = useNavigate();

    const handleToggleCompare = (id: number) => {
        const isInCompareList = compareArray.includes(id);
        toggleCompareArray(id);
        setSuccessMessage(
            isInCompareList ? (
                <Locale localeId={IdlocaGeneric.item_removed_from_compare_list}/>
            ) : (
                <Locale localeId={IdlocaGeneric.item_added_to_compare_list}/>
            ),
        );
        setOpenSnackbar(true);
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const handleRedirectToCompare = () => {
        navigate("/catalogue/compare");
    };

    const handleToggleFavorite = async (motorcycleId: number) => {
        toggleFavourites(motorcycleId);
    };

    if (dataLoading) {
        return (
            <Grid item xs={12} sx={{display: "flex", justifyContent: "center", paddingTop: "2rem"}}>
                <CircularProgress/>
            </Grid>
        );
    }

    const renderContent = () => {
        if (!motorcycles || motorcycles.length === 0) {
            return (
                <Typography variant="h6" color="textSecondary">
                    <InfoMessage messageLocaId={IdlocaGeneric.no_data} type="info"/>
                </Typography>
            );
        }

        return (
            <Grid container direction="row" spacing={2} justifyContent="center">
                {motorcycles.map((motorcycle) => (
                    <CatalogueTableItem
                        key={motorcycle.id}
                        motorcycle={motorcycle}
                        handleToggleCompare={handleToggleCompare}
                        handleToggleFavorite={handleToggleFavorite}
                        viewStyle={viewStyle}
                        favourites={favourites}
                    />
                ))}
            </Grid>
        );
    };

    const content = renderContent();

    return (
        <>
            {content}
            <CustomSnackbar
                open={openSnackbar}
                message={successMessage}
                onClose={handleCloseSnackbar}
                actionLocaId={IdlocaGeneric.view_compare_list}
                onActionClick={handleRedirectToCompare}
                severity="success"
            />
        </>
    );
};
